import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api, getSessionCookieToken, type OrgRemediationSettings } from '../api/client'
import LoadingOverlay from './LoadingOverlay'

const DEFAULT_SETTINGS: OrgRemediationSettings = {
  stack_description: '',
}

// Mirrors MAX_STACK_DESCRIPTION_CHARS on the backend.
const MAX_CHARS = 8000

const PLACEHOLDER = `Example:

Architecture: Next.js 15 frontend on Vercel, FastAPI backend on Railway, Postgres (Neon) + Redis (Upstash).
Languages: TypeScript (web), Python 3.13 (api).
Conventions: backend uses uv + ruff; run "make test" before changes; never edit migrations/ by hand.
Infra: deploys via GitHub Actions; secrets in AWS Secrets Manager.
Anything else the fix agent should know about how the system fits together.`

export default function OrgRemediationSettingsPanel() {
  const { getToken } = useAuth()
  const [settings, setSettings] = useState<OrgRemediationSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  async function authToken(): Promise<string> {
    const t = getSessionCookieToken() ?? (await getToken())
    if (!t) throw new Error('Not authenticated')
    return t
  }

  useEffect(() => {
    ;(async () => {
      try {
        const t = await authToken()
        const result = await api.getRemediationSettings(t)
        setSettings(result)
      } catch {
        // Fall back to defaults; the user can still save.
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function update(value: string) {
    setSettings({ stack_description: value })
    setSaveOk(false)
    setSaveError(null)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaveOk(false)
    setSaveError(null)
    try {
      const t = await authToken()
      const saved = await api.saveRemediationSettings(t, settings)
      setSettings(saved)
      setSaveOk(true)
    } catch (err) {
      setSaveError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <LoadingOverlay minHeight="240px" />
  }

  const remaining = MAX_CHARS - settings.stack_description.length

  return (
    <section className="notif-settings-panel notif-settings-panel--wide">
      <div className="notif-settings-header">
        <h2 className="integration-section-title" style={{ margin: 0 }}>Stack Description</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
          Describe your complete integrated stack — services, languages, infrastructure, and
          conventions. SelfHeal passes this to the code-fix agent as system context so it
          understands your system before proposing a fix. If your repo ships a{' '}
          <code>CLAUDE.md</code> or <code>AGENTS.md</code>, those are read automatically and
          combined with this description.
        </p>
      </div>

      <form onSubmit={handleSave} className="notif-settings-form">
        <fieldset className="notif-settings-group">
          <legend className="notif-settings-legend">System context</legend>
          <div className="notif-settings-row-input">
            <label className="notif-settings-label-stacked" htmlFor="stack-description">
              Stack overview
            </label>
            <textarea
              id="stack-description"
              className="cred-input"
              style={{
                width: '100%',
                minHeight: '220px',
                marginTop: '4px',
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: '13px',
                lineHeight: 1.5,
                resize: 'vertical',
              }}
              value={settings.stack_description}
              onChange={e => update(e.target.value.slice(0, MAX_CHARS))}
              placeholder={PLACEHOLDER}
            />
            <p
              className="notif-settings-hint"
              style={{ marginTop: '4px', textAlign: 'right', color: remaining < 0 ? 'var(--danger)' : 'var(--text-muted)' }}
            >
              {remaining.toLocaleString()} characters remaining
            </p>
          </div>
        </fieldset>

        {saveError && <p className="cred-error" style={{ margin: 0 }}>{saveError}</p>}
        {saveOk && <p className="cred-success" style={{ margin: 0 }}>Stack description saved.</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="cred-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save stack description'}
          </button>
        </div>
      </form>
    </section>
  )
}
